import { GEAR_EFFECT_FX, RELIC_AWAKENED_FX, UNIQUES, type RelicId } from '@somnhold/content';
import { hexToWorld } from '../hex.ts';
import { dist2, idiv, mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero, Structure } from '../types.ts';
import { damageEnemy, enemiesNear, ev, targetable } from './combat.ts';
import { raiseShield } from './kits.ts';
import { isBoss, slowFor } from './towers.ts';

/**
 * The rare gear effects added on 2026-10-25 (GEAR_EFFECT_FX) and the awakened relics
 * (RELIC_AWAKENED_FX). The older effects (burn, lifesteal, thorns, swift) live in combat.ts.
 */

/** Whether a carried relic was awakened at the forge. */
export const awake = (h: Hero, r: RelicId): boolean => (h.awakened?.includes(r) ?? false) && h.relics.includes(r);

/** An elite, a boss or a crypt's guardian. */
export const bigFoe = (e: Enemy): boolean => e.elite || e.un !== undefined || isBoss(e) || e.kind === 'warden' || e.kind === 'magmaLord' || e.kind === 'colossus';

/** Fury, executioner, a mace (on the big ones) and the gate blade (on the stunned): what a basic attack's blow becomes against `e` (before armor). */
export const gearBlow = (h: Hero, e: Enemy, dmg: number): number => {
  let out = dmg;
  if (h.stats.bigFoeBp && bigFoe(e)) out = mulBp(out, 10000 + h.stats.bigFoeBp);
  if (e.stun > 0 && h.uq?.includes('gateBlade')) out = mulBp(out, 10000 + (UNIQUES.gateBlade.fx.stunnedBp as number));
  if (h.stats.furyGearBp && h.hp * 10000 < h.stats.maxHp * GEAR_EFFECT_FX.fury.belowBp) out = mulBp(out, 10000 + h.stats.furyGearBp);
  if (h.stats.executeBp && e.hp * 10000 < e.maxHp * GEAR_EFFECT_FX.execute.belowBp) out = mulBp(out, 10000 + h.stats.executeBp);
  return out;
};

/** How far behind its target a heavy crossbow's bolt carries on (sim units). */
const PIERCE_REACH = 1000;

/**
 * After a basic attack landed on its target for `dealt`: frost slows it, cleave hits around it, and
 * every few blows the chain arcs to the nearest others. Only the attack's own target triggers these
 * (a mage's splash doesn't cleave or chain once per nightmare caught in it).
 */
export const gearAfterHit = (s: BattleState, h: Hero, e: Enemy, dealt: number): void => {
  const st = h.stats;
  // The events below only tell the renderer what happened (they are not hashed).
  if (st.executeBp && (e.hp + dealt) * 10000 < e.maxHp * GEAR_EFFECT_FX.execute.belowBp) ev(s, { t: 'shot', from: h.id, to: e.id, kind: 'gearExecute', x: h.x, y: h.y, tx: e.x, ty: e.y });
  if (st.frostBp && e.hp > 0) {
    const sl = slowFor(e, st.frostBp);
    if (e.slowBp < sl) e.slowBp = sl;
    if (e.slowTicks < GEAR_EFFECT_FX.frost.ticks) e.slowTicks = GEAR_EFFECT_FX.frost.ticks;
    ev(s, { t: 'boom', x: e.x, y: e.y, r: 600, kind: 'gearFrost' });
  }
  if (st.cleaveBp) {
    const fx = GEAR_EFFECT_FX.cleave;
    const part = mulBp(dealt, st.cleaveBp);
    if (part > 0) {
      const around = enemiesNear(s, e.x, e.y, fx.radius, (x) => x.id !== e.id && targetable(s, x));
      // The swing, from the hero through its target, then a cut on each nightmare it carried into.
      if (around.length) ev(s, { t: 'shot', from: h.id, to: e.id, kind: 'gearCleave', x: h.x, y: h.y, tx: e.x, ty: e.y });
      for (const o of around) {
        damageEnemy(s, o, part, { pid: h.pid, ignoresArmor: true });
        ev(s, { t: 'shot', from: e.id, to: o.id, kind: 'gearCleaveHit', x: e.x, y: e.y, tx: o.x, ty: o.y });
      }
    }
  }
  // A maul: every few blows that land stun the target (half on elites; a boss or a guardian shrugs it off).
  if (st.stunEvery && e.hp > 0) {
    h.stunHits = (h.stunHits ?? 0) + 1;
    if (h.stunHits >= st.stunEvery) {
      h.stunHits = 0;
      const t = bigFoe(e) ? (e.elite ? idiv(st.stunTicks ?? 0, 2) : 0) : (st.stunTicks ?? 0);
      if (t > e.stun) e.stun = t;
      if (t > 0) ev(s, { t: 'boom', x: e.x, y: e.y, r: 500, kind: 'gearStun' });
    }
  }
  // A heavy crossbow: the bolt carries on into the nearest nightmare behind its target (farther from the hero).
  if (st.pierceGear && dealt > 0) {
    const d0 = dist2(h.x, h.y, e.x, e.y);
    let best: Enemy | null = null;
    let bd = 0;
    for (const o of enemiesNear(s, e.x, e.y, PIERCE_REACH, (x) => x.id !== e.id && targetable(s, x))) {
      if (dist2(h.x, h.y, o.x, o.y) <= d0) continue;
      const d = dist2(o.x, o.y, e.x, e.y);
      if (!best || d < bd || (d === bd && o.id < best.id)) {
        best = o;
        bd = d;
      }
    }
    if (best) {
      ev(s, { t: 'shot', from: e.id, to: best.id, kind: 'gearPierce', x: e.x, y: e.y, tx: best.x, ty: best.y });
      damageEnemy(s, best, dealt, { pid: h.pid, ignoresArmor: true });
    }
  }
  if (st.chainBp) {
    const fx = GEAR_EFFECT_FX.chain;
    h.gearHits = (h.gearHits ?? 0) + 1;
    if (h.gearHits >= fx.every) {
      h.gearHits = 0;
      const part = mulBp(dealt, st.chainBp);
      // The nearest others first (enemiesNear goes by id: sort by distance, then id).
      const near = enemiesNear(s, e.x, e.y, fx.range, (x) => x.id !== e.id && targetable(s, x))
        .map((o) => ({ o, d: dist2(o.x, o.y, e.x, e.y) }))
        .sort((a, b) => a.d - b.d || a.o.id - b.o.id);
      let from = e;
      for (const { o } of near.slice(0, fx.targets)) {
        ev(s, { t: 'shot', from: from.id, to: o.id, kind: 'gearChain', x: from.x, y: from.y, tx: o.x, ty: o.y });
        if (part > 0) damageEnemy(s, o, part, { pid: h.pid, ignoresArmor: true, type: 'lightning' });
        from = o;
      }
    }
  }
};

/** Every tick a standing hero: the guard's shield rises on its beat; an awakened grimoire speeds the ultimate. */
export const gearHeroTick = (s: BattleState, h: Hero): void => {
  if (h.stats.guardBp) {
    const fx = GEAR_EFFECT_FX.guard;
    if (s.tick % fx.every === 0 && (h.shield ?? 0) <= 0) {
      raiseShield(h, mulBp(h.stats.maxHp, h.stats.guardBp), fx.ticks, false);
      ev(s, { t: 'boom', x: h.x, y: h.y, r: 600, kind: 'gearGuard' });
    }
  }
  if (h.control === 'live' && awake(h, 'vigilGrimoire') && (h.skillCd[2] as number) > 0) {
    // ultCdBp faster: one extra tick off every (10000 / ultCdBp) ticks.
    const every = idiv(10000, RELIC_AWAKENED_FX.vigilGrimoire.ultCdBp);
    if (s.tick % every === 0) h.skillCd[2] = (h.skillCd[2] as number) - 1;
  }
};

/** A nightmare felled by `h`: echo takes time off its skills' recharge (its wisp flies from `e` to the hero). */
export const gearOnKill = (s: BattleState, h: Hero, e?: Enemy): void => {
  const t = h.stats.echoTicks ?? 0;
  if (t <= 0) return;
  let any = false;
  for (let i = 0; i < 3; i++) {
    const cd = h.skillCd[i] as number;
    if (cd <= 0) continue;
    h.skillCd[i] = cd > t ? cd - t : 0;
    any = true;
  }
  if (any) ev(s, { t: 'shot', from: e?.id ?? h.id, to: h.id, kind: 'gearEcho', x: e?.x ?? h.x, y: e?.y ?? h.y, tx: h.x, ty: h.y });
};

/** The best banner standing near a tower (bp more damage; banners don't add up). */
export const bannerBp = (s: BattleState, st: Structure): number => {
  let best = 0;
  const c = hexToWorld(st.q, st.r);
  for (const h of s.heroes) {
    const bp = h.stats.bannerBp ?? 0;
    if (bp <= best || h.downTicks > 0) continue;
    if (withinRange(c.x, c.y, h.x, h.y, GEAR_EFFECT_FX.banner.range)) best = bp;
  }
  return best;
};

/** How many times a wave a "once a wave" relic works: twice when awakened. */
export const relicUses = (h: Hero, r: 'vigilGrimoire'): number => (awake(h, r) ? RELIC_AWAKENED_FX[r].uses : 1);
